import { useSession } from "@/services/auth0/session";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PaymentMethod } from "@/api.types";
import { usePaymentMethods } from "@/api/use-payment-methods-api";
import { Loader2, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";
import {
  useMakePaymentMethodDefault,
  useDeletePaymentMethod,
} from "@/api/use-payment-methods-api";
import { useToast } from "@/hooks/use-toast";
import { CreateBusinessAccountDialog } from "./create-business-account.dialog";
import { BillingAddressDialog } from "./billing-address.dialog";
import { AddPaymentMethodDialog } from "./add-payment-method.dialog";
import { EditProfileDialog } from "./edit-profile.dialog";

export default function SettingsScreen() {
  const { user, customer } = useSession();
  const { data: billingData, isLoading: isLoadingPaymentMethods } =
    usePaymentMethods();
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [isCreateBusinessAccountOpen, setIsCreateBusinessAccountOpen] =
    useState(false);
  const [isBillingAddressOpen, setIsBillingAddressOpen] = useState(false);
  const [isAddPaymentMethodOpen, setIsAddPaymentMethodOpen] = useState(false);
  const { toast } = useToast();

  // Effect to explicitly reset pointer-events on body when component mounts/updates
  useEffect(() => {
    // Check if the style is currently set to none
    if (document.body.style.pointerEvents === "none") {
      console.log(
        "[SettingsScreen Effect] Removing pointer-events: none from body"
      );
      document.body.style.pointerEvents = ""; // Reset the style
    }
    // Optionally, return a cleanup function if needed, though likely not necessary here
    // return () => { /* potential cleanup */ };
  }); // Run on every render to be safe, or add dependencies if specific triggers are known

  const setDefaultPaymentMethodMutation = useMakePaymentMethodDefault({
    onSuccess: () => {
      toast({
        title: "Default payment method updated",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error updating default payment method",
      });
    },
  });

  const deletePaymentMethodMutation = useDeletePaymentMethod({
    onSuccess: () => {
      toast({
        title: "Payment method removed",
      });
    },
    onError: () => {
      toast({
        variant: "destructive",
        title: "Error removing payment method",
      });
    },
  });

  // Create a function to get the first letter of customer's first name
  const getInitial = () => {
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return "";
  };

  // Format full name
  const getFullName = () => {
    if (user) {
      return `${user.firstName} ${user.lastName}`;
    }
    return "";
  };

  // Format billing address as one line
  const formatBillingAddress = () => {
    if (!customer?.billing?.address) return "No billing address";

    const address = customer.billing.address;
    const parts = [
      address.line1,
      address.line2,
      address.city,
      address.province,
      address.postalCode,
      address.country,
    ].filter(Boolean);

    return parts.join(", ");
  };

  // Get card brand logo
  const getCardBrandLogo = (brand?: string): string => {
    const baseUrl =
      "https://raw.githubusercontent.com/datatrans/payment-logos/master/assets/cards";
    switch (brand?.toLowerCase()) {
      case "mastercard":
        return `${baseUrl}/mastercard.svg`;
      case "visa":
        return `${baseUrl}/visa.svg`;
      case "amex":
      case "american express":
        return `${baseUrl}/american-express.svg`;
      case "diners":
      case "diners club":
        return `${baseUrl}/diners.svg`;
      case "discover":
        return `${baseUrl}/discover.svg`;
      case "jcb":
        return `${baseUrl}/jcb.svg`;
      case "unionpay":
        return `${baseUrl}/unionpay.svg`;
      default:
        return `${baseUrl}/card-generic.svg`;
    }
  };

  return (
    <div className="max-w-7xl">
      <h1 className="text-3xl font-bold">Settings</h1>
      <p className="text-muted-foreground mt-2">
        Manage your account settings and preferences
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Basic Information</CardTitle>
              <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-medium">
                {getInitial()}
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">Name</span>
                <span className="text-muted-foreground">{getFullName()}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium">Email</span>
                <span className="text-muted-foreground">{user?.email}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="font-medium">Phone</span>
                <span className="text-muted-foreground">
                  {user?.phone ?? "Not provided"}
                </span>
              </div>

              <div className="flex justify-end mt-4">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsEditProfileOpen(true)}
                >
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Business</CardTitle>
            </CardHeader>

            <CardContent>
              {customer?.businessAccount ? (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Company Name</span>
                    <span className="text-muted-foreground">
                      {customer.businessAccount.companyName}
                    </span>
                  </div>

                  {customer.businessAccount.taxNumber && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Tax Number</span>
                      <span className="text-muted-foreground">
                        {customer.businessAccount.taxNumber}
                      </span>
                    </div>
                  )}

                  {customer.businessAccount.registrationNo && (
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Registration No</span>
                      <span className="text-muted-foreground">
                        {customer.businessAccount.registrationNo}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-end mt-4">
                    <Button
                      variant="outline"
                      className="w-full"
                      onClick={() => setIsCreateBusinessAccountOpen(true)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-muted-foreground text-center">
                        No business account connected
                      </p>
                    </CardContent>
                  </Card>
                  <div className="flex justify-center mt-4">
                    <Button
                      className="w-full"
                      onClick={() => setIsCreateBusinessAccountOpen(true)}
                    >
                      Add Business Account
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Billing and Payment</CardTitle>
            </CardHeader>

            <CardContent className="space-y-8">
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Billing Address</h3>
                <div className="flex justify-between items-center">
                  <p className="text-muted-foreground">
                    {formatBillingAddress()}
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => setIsBillingAddressOpen(true)}
                  >
                    {customer?.billing?.address ? "Edit" : "Add"}
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Payment Methods</h3>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddPaymentMethodOpen(true)}
                  >
                    Add
                  </Button>
                </div>

                {isLoadingPaymentMethods ? (
                  <div className="flex justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : !billingData?.paymentMethods?.length ? (
                  <Card>
                    <CardContent className="p-6">
                      <p className="text-muted-foreground">
                        No payment methods added yet.
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {billingData.paymentMethods
                      .sort((a, b) => (a.isDefault ? -1 : b.isDefault ? 1 : 0))
                      .map((method: PaymentMethod) => (
                        <Card key={method.id} className="relative">
                          {method.isDefault && (
                            <div className="absolute top-0 left-1/2 -translate-y-1/2 -translate-x-1/2">
                              <Badge
                                variant="outline"
                                className="text-xs font-normal bg-white"
                              >
                                Default
                              </Badge>
                            </div>
                          )}
                          <CardContent className="p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0 sm:justify-between">
                              <div className="flex items-center gap-3">
                                <img
                                  src={getCardBrandLogo(method.brand)}
                                  alt={method.brand ?? "Card"}
                                  className="h-8 w-auto"
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">
                                    •••• {method.last4}
                                  </span>
                                  <p className="text-sm text-muted-foreground">
                                    Expires {method.expiryMonth}/
                                    {method.expiryYear}
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-row gap-2 self-end sm:self-auto">
                                {!method.isDefault && (
                                  <Button
                                    variant="outline"
                                    className="whitespace-nowrap"
                                    onClick={() =>
                                      setDefaultPaymentMethodMutation.mutate(
                                        method.id
                                      )
                                    }
                                  >
                                    Make Default
                                  </Button>
                                )}
                                <Button
                                  variant="destructive"
                                  onClick={() =>
                                    deletePaymentMethodMutation.mutate(
                                      method.id
                                    )
                                  }
                                >
                                  Remove
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                  </div>
                )}
                <div className="flex justify-end mt-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <img
                      src="https://www.madebytribe.com/wp-content/uploads/2023/12/2035148938.png"
                      alt="Powered by Stripe"
                      className="h-8 w-auto"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>API Keys</CardTitle>
              <Badge variant="outline" className="ml-2">
                Coming Soon
              </Badge>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4">
                Use API keys to authenticate requests to the EZX API. Treat them
                like passwords; do not share them publicly.
              </p>
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col items-center justify-center py-4">
                    <AlertTriangle className="h-8 w-8 text-amber-500 mb-2" />
                    <p className="text-muted-foreground text-center">
                      API key management functionality is coming soon.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Dialogs only render when open */}
      {isEditProfileOpen && user && (
        <EditProfileDialog
          open={isEditProfileOpen}
          onOpenChange={setIsEditProfileOpen}
          user={user}
        />
      )}
      {isCreateBusinessAccountOpen && (
        <CreateBusinessAccountDialog
          open={isCreateBusinessAccountOpen}
          onOpenChange={setIsCreateBusinessAccountOpen}
        />
      )}
      {isBillingAddressOpen && (
        <BillingAddressDialog
          open={isBillingAddressOpen}
          onOpenChange={setIsBillingAddressOpen}
          customerId={customer?.id ?? ""}
          billingInfo={customer?.billing}
        />
      )}
      {isAddPaymentMethodOpen && (
        <AddPaymentMethodDialog
          open={isAddPaymentMethodOpen}
          onOpenChange={setIsAddPaymentMethodOpen}
        />
      )}
    </div>
  );
}
